import { Avatar } from 'antd';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { userSignOut } from '@src/appRedux/actions/Auth';
import { IUserClaims, IUserProfile } from '@src/models/interfaces/user.interface';
import { BACKOFFICE_BASE_PATH, usePlatformRole } from '@src/packages/backoffice';
import { getFullUrl } from '@src/packages/utils';
import { PATHS as USER_PATHS } from '@src/routes/user/constants.js';

import { getNameInitials } from './getNameInitials';
import styles from './index.module.less';

type SidebarBottomProps = {
  collapsed?: boolean;
  desktopTheme?: boolean;
};

export const SidebarBottom: React.FC<SidebarBottomProps> = ({
  collapsed = false,
  desktopTheme = false,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { pathname } = useLocation();
  const intl = useIntl();

  const isProfileActive = pathname.includes('/user/profile');

  const userClaims = useSelector(({ user }: { user: IUserClaims }) => user.access.data?.claims);
  const currentUser = useSelector(({ user }: { user: IUserProfile }) => user.profile.data);
  const fullName = [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const profileLabel = fullName || intl.formatMessage({ id: 'sidebar.user.info' });
  const logoutLabel = intl.formatMessage({ id: 'button.logout' });
  const backofficeLabel = intl.formatMessage({ id: 'backoffice.title' });
  const { role: platformRole } = usePlatformRole();

  const handleProfileRedirect = () => {
    history.push(getFullUrl(USER_PATHS.PROFILE, url));
  };

  const handleBackofficeRedirect = () => {
    history.push(BACKOFFICE_BASE_PATH);
  };

  const handleLogout = () => {
    dispatch(userSignOut());
  };

  return (
    <>
      <div className={classnames(styles.userInfoItem, desktopTheme && styles.desktopTheme)}>
        <button
          aria-current={isProfileActive ? 'page' : undefined}
          aria-label={profileLabel}
          className={classnames(styles.userInfoAction, { [styles.active]: isProfileActive })}
          onClick={handleProfileRedirect}
          title={profileLabel}
          type="button"
        >
          <Avatar
            alt=""
            className={classnames('gx-avatar', styles.avatar)}
            src={userClaims?.picture || currentUser?.photoURL}
          >
            {getNameInitials(currentUser?.firstName, currentUser?.lastName)}
          </Avatar>
        </button>
        {platformRole ? (
          <button
            aria-hidden={collapsed}
            aria-label={backofficeLabel}
            className={classnames(styles.userInfoAction, { [styles.hidden]: collapsed })}
            onClick={handleBackofficeRedirect}
            tabIndex={collapsed ? -1 : undefined}
            title={backofficeLabel}
            type="button"
          >
            <span aria-hidden className="material-symbols-outlined">
              admin_panel_settings
            </span>
          </button>
        ) : null}
        <button
          aria-hidden={collapsed}
          aria-label={logoutLabel}
          className={classnames(styles.userInfoAction, styles.logout, desktopTheme && styles.logoutDesktop, {
            [styles.hidden]: collapsed,
          })}
          onClick={handleLogout}
          tabIndex={collapsed ? -1 : undefined}
          title={logoutLabel}
          type="button"
        >
          <span aria-hidden className={classnames('material-symbols-outlined', styles.logoutIcon)}>
            logout
          </span>
          <span className={styles.logoutLabel}>{logoutLabel}</span>
        </button>
      </div>
      <div
        className={classnames(styles.version, desktopTheme && styles.versionDesktop, {
          [styles.hidden]: collapsed,
        })}
      >
        {typeof SYSTEM !== 'undefined' && SYSTEM.version ? (
          <small>
            {intl.formatMessage({ id: 'sidebar.nav.version' }, { version: SYSTEM.version })}
          </small>
        ) : null}
      </div>
    </>
  );
};
